/**
 * Slide tile color palette — Session 39 (v2, vibrant)
 *
 * Rich, futuristic colors for slide navigation tiles. Matches the
 * intensity of font-preset accent colors and template highlights.
 * Each color is a fully saturated "signal" hue — the visual restraint
 * comes from alpha blending on the dark chassis, NOT from desaturating
 * the source color.
 *
 * Each slide gets a deterministic color derived from its stable `id`,
 * so the color travels with the slide when dragged.
 */

export const SLIDE_TILE_COLORS = [
  { hex: '#00d4ff', rgb: '0, 212, 255' },    // 0  cyan
  { hex: '#3b82f6', rgb: '59, 130, 246' },   // 1  electric blue
  { hex: '#8b5cf6', rgb: '139, 92, 246' },   // 2  violet
  { hex: '#b026ff', rgb: '176, 38, 255' },   // 3  purple
  { hex: '#ff007f', rgb: '255, 0, 127' },    // 4  magenta
  { hex: '#ff6b99', rgb: '255, 107, 153' },  // 5  hot pink
  { hex: '#ff3333', rgb: '255, 51, 51' },    // 6  red
  { hex: '#ff6b2b', rgb: '255, 107, 43' },   // 7  orange
  { hex: '#ffd700', rgb: '255, 215, 0' },    // 8  gold
  { hex: '#a8eb12', rgb: '168, 235, 18' },   // 9  lime
  { hex: '#00e5a0', rgb: '0, 229, 160' },    // 10 emerald
  { hex: '#00d4cc', rgb: '0, 212, 204' },    // 11 teal
];

/**
 * Simple, fast, stable 32-bit string hash (FNV-1a variant).
 * Same input always yields same output — critical so color persists
 * across sessions once the slide id is saved.
 */
function hashString(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/**
 * Deterministic color for a slide.
 *
 * Priority:
 *   1. `slide.colorIndex` — the explicit palette index assigned at
 *      slide creation time (guarantees distinct colors across the set).
 *   2. Stable hash of `slide.id` — used only as a fallback for legacy
 *      slides that haven't yet been migrated by ensureSlideIds().
 *   3. `fallbackIndex` — positional default.
 *
 * @param {{id?: string, colorIndex?: number}|null|undefined} slide
 * @param {number} fallbackIndex
 * @returns {{hex: string, rgb: string}}
 */
export function getTileColor(slide, fallbackIndex = 0) {
  if (slide && typeof slide === 'object') {
    if (typeof slide.colorIndex === 'number') {
      const idx = ((slide.colorIndex % SLIDE_TILE_COLORS.length) + SLIDE_TILE_COLORS.length) % SLIDE_TILE_COLORS.length;
      return SLIDE_TILE_COLORS[idx];
    }
    if (slide.id) {
      const idx = hashString(slide.id) % SLIDE_TILE_COLORS.length;
      return SLIDE_TILE_COLORS[idx];
    }
  }
  return SLIDE_TILE_COLORS[fallbackIndex % SLIDE_TILE_COLORS.length];
}
